import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Typography,
  Form,
  Button,
  Layout,
  Divider,
  message,
  Select,
  Alert,
  Input,
  Progress,
  Tooltip,
} from "antd";
import {
  WhatsAppOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { validateCPF } from "../../helpers/validators";
import InputMask from "../../components/inputMask";
import { useNavigate } from "react-router-dom";
import { changeRecuperacaoSenha, requestRecuperacaoSenha } from "../../api/autenticacao";
import { validatePassword } from "../../helpers/validatePassword";
import { IMAGES } from "../../constants/images";

const { Title, Text, Link } = Typography;

const styles = {
  row: {
    flexGrow: 1,
  },
  textWhite: {
    color: "#ffffff",
  },
};

const metodos = [
  {
    value: 1,
    label: "E-mail",
  },
  {
    value: 2,
    label: "Celular (SMS)",
  },
];

const title = (
  <Space direction="vertical">
    <Text style={styles?.textWhite}>• Minimo de 8 characters.</Text>
    <Text style={styles?.textWhite}>• Pelo menos 1 letra em maiúsculo.</Text>
    <Text style={styles?.textWhite}>• Pelo menos 1 letra em minúsculo.</Text>
    <Text style={styles?.textWhite}>• Pelo menos 1 numero.</Text>
    <Text style={styles?.textWhite}>• Pelo menos 1 character especial (@,.,$,#).</Text>
  </Space>
);

const validator = {
  cpf: [
    { required: true, message: "Informe o CPF!" },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || validateCPF(getFieldValue("cpf"))) {
          return Promise.resolve();
        }
        return Promise.reject("Informe um CPF válido!");
      },
    }),
  ],
  metodo: [{ required: true, message: "Selecione o método!" }],
};

const validator_change = {
  codigo: [{ required: true, message: "Informe o código!" }],
  senha: [
    { required: true, message: "Informe a nova senha!" },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || validatePassword(getFieldValue("senha"))[0] >= 75) {
          return Promise.resolve();
        }
        return Promise.reject("Senha muito fraca!");
      },
    }),
  ],
  senha_confirmation: [
    { required: true, message: "Informe novamente a senha!" },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || getFieldValue("senha") === value) {
          return Promise.resolve();
        }
        return Promise.reject("As senhas não conferem!");
      },
    }),
  ],
};

const RecuperarSenha = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [passwordLevel, setPasswordLevel] = useState({});
  const [cpf, setCpf] = useState(null);
  const [mensagem, setMensagem] = useState("");

  const solicitarRecuperacao = async (formulario) => {
    setLoading(true);
    try {
      const retorno = await requestRecuperacaoSenha(formulario);
      if (retorno?.statusCode === 200) {
        setCpf(formulario?.cpf);
        setMensagem(`Token enviado para ${retorno?.response?.data?.destinatario}`);
        message.success(`Token enviado para ${retorno?.response?.data?.destinatario}`);
        form.resetFields();
      } else {
        message.error(retorno?.response?.message);
      }
    } catch (error) {
      message?.error("Não foi possível atender a requisição!");
    }
    setLoading(false);
  };

  const recuperarSenha = async (formulario) => {
    setLoading(true);
    try {
      const retorno = await changeRecuperacaoSenha(formulario);
      if (retorno?.statusCode === 200) {
        message.success("Senha alterada com sucesso!");
        navigate(-1);
      } else {
        message.error(retorno?.response?.message);
      }
    } catch (error) {
      message?.error("Não foi possível atender a requisição!");
    }
    setLoading(false);
  };

  const onSubmitSolicitacao = (formulario) => {
    solicitarRecuperacao({
      cpf: formulario?.cpf?.replace(/[^\d]+/g, ""),
      metodo: formulario?.metodo,
    });
  };

  const onSubmitRecuperacao = (formulario) => {
    if (!cpf) return;
    formulario.cpf = cpf?.replace(/[^\d]+/g, "");
    recuperarSenha(formulario);
  };

  const formSolicitacao = () => {
    return (
      <Form layout="vertical" autoComplete="off" onFinish={onSubmitSolicitacao}>
        <Form.Item name="cpf" label="CPF" hasFeedback rules={validator?.cpf}>
          <InputMask mask="999.999.999-99" size="large" placeholder="Informe o CPF" disabled={loading} />
        </Form.Item>

        <Form.Item name="metodo" label="Metodo" hasFeedback rules={validator?.metodo}>
          <Select
            size="large"
            disabled={loading}
            placeholder="Selecione o método"
            className="full-width"
            options={metodos}
          />
        </Form.Item>

        <Form.Item className="FormItem">
          <Button size="large" type="primary" className="full-width" loading={loading} htmlType="submit">
            Enviar
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const formRecuperacao = () => {
    return (
      <Form form={form} onFinish={onSubmitRecuperacao} layout="vertical" autoComplete="off">
        <Text>{mensagem}</Text>
        <Divider />
        <Form.Item name="codigo" label="Código" rules={validator_change?.codigo} hasFeedback>
          <Input size="large" disabled={loading} placeholder="Informe o código" />
        </Form.Item>

        <Form.Item className="FormItem" label="Nova Senha" name="senha" rules={validator_change?.senha} hasFeedback>
          <Input.Password
            size="large"
            onChange={(val) => setPasswordLevel(validatePassword(val?.target?.value))}
            className="input"
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Digite a nova senha"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined style={{ color: "#000" }} />)}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          className="FormItem"
          label="Repetir Nova Senha"
          name="senha_confirmation"
          rules={validator_change?.senha_confirmation}
          hasFeedback>
          <Input.Password
            size="large"
            className="input"
            prefix={<LockOutlined className="site-form-item-icon" />}
            placeholder="Digite novamente a senha"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined style={{ color: "#000" }} />)}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item className="FormItem">
          <Text type="secondary">
            Força da senha{" "}
            <Tooltip title={title}>
              <InfoCircleOutlined />
            </Tooltip>
          </Text>
          <Progress
            percent={passwordLevel[0]}
            size="small"
            showInfo={false}
            strokeColor={passwordLevel[1]}
            status="exception"
          />
        </Form.Item>

        <Form.Item className="FormItem">
          <Button
            size="large"
            loading={loading}
            type="primary"
            htmlType="submit"
            style={{ width: "100%" }}
            className="login-form-button">
            Enviar
          </Button>
        </Form.Item>
      </Form>
    );
  };

  return (
    <Row justify="center" align="middle" className="full-width" style={styles?.row}>
      <Col xs={23} sm={18} md={14} lg={12} xl={8} xxl={6}>
        <Card>
          <Space direction="vertical" className="full-width" size={20}>
            <Layout.Content align="center">
              <img width={140} src={IMAGES?.recuperacaoSenha} className="resize-contain" />
            </Layout.Content>

            <Title className="title" align="center">
              Recuperar Senha
            </Title>

            {cpf > 0 ? formRecuperacao() : formSolicitacao()}

            <Alert
              description={
                <Text>
                  Caso tenha algum problema na solicitação do código, entre em contato com o setor de RH ou TI:
                  <br />
                  <Link target="_blank" underline href="https://wa.me/message/WKIFUWDNKN2FG1">
                    <WhatsAppOutlined /> (49) 9 9945-0650
                  </Link>
                </Text>
              }
              type="info"
              showIcon
            />
          </Space>
          <Divider />
          <Form.Item className="FormItem">
            <Button
              icon={<ArrowLeftOutlined />}
              size="large"
              onClick={() => {
                if (cpf > 0) {
                  setCpf(null);
                  setMensagem("");
                } else {
                  navigate("/login");
                }
              }}
              loading={loading}>
              Voltar
            </Button>
          </Form.Item>
        </Card>
      </Col>
    </Row>
  );
};

export default RecuperarSenha;
